/**
 * ÉN TILSTAND, TEGNET ETTER §0.4 — pille når den krever noe, hul prikk når den ikke gjør det.
 *
 * ---------------------------------------------------------------------------------------------
 * HVORFOR DENNE FINNES, OG HVA SOM STO HER FØR.
 *
 * Atle, 10. september (ARTISTS.md §0.4): «Colour is for states that need action. Available, Hold,
 * Confirmed and Unavailable get a pill. Everything neutral — Not answered, Asked, Not listed —
 * drops to muted text with a small hollow dot. The current wall of identical grey pills is what
 * this replaces.»
 *
 * Regelen ble bygget 10. september — som en TERNÆR INNE I ÉN TABELLCELLE, i byråets artistliste.
 * Alle andre statuskolonner i produktet fortsatte å tegne `tone === 'neutral'` som en grå pille,
 * og 11. september målte jeg det: `PERFORMANCE_STATUS_TONE` ga seks av ti verdier `neutral`, altså
 * seks grå piller under hverandre i en lineup — nøyaktig veggen §0.4 skulle fjerne.
 *
 * Atle, 11. september: «se til at siden har lik design med god UX, ikoner og slikt over hele
 * siden. Særlig etter siste mockup runde har du operert med forskjellige stiler her og der.»
 *
 * Årsaken var ikke at noen glemte regelen. Det var at det ikke fantes noe å gjenbruke: regelen
 * bodde i en ternær, og en ternær kan ikke importeres. `CLAUDE.md` sier det rett ut — skriver du
 * den andre kopien av et tverrgående hensyn, stopp. Dette er det ene stedet.
 *
 * ---------------------------------------------------------------------------------------------
 * TONEN ER FORTSATT KARTETS ANSVAR, IKKE DENNES.
 *
 * `PERFORMANCE_STATUS_TONE` og de andre `Record<Status, BadgeTone>`-kartene bestemmer fortsatt hva
 * hver verdi ER. Denne komponenten bestemmer bare hvordan `neutral` SER UT — og det er skillet som
 * gjør at en ny enumverdi fortsatt blir en typefeil i kartet sitt framfor å bli tegnet stille som
 * noe den ikke er.
 */
import type { ReactNode } from 'react';

import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { QuietState } from '@/components/ui/QuietState';

export interface StatusTagProps {
  tone: BadgeTone;
  children: ReactNode;
  /**
   * HVA `neutral` SKAL SE UT SOM HER. Standard er den hule prikken, og den er §0.4.
   *
   * `pill` finnes for den ene formen §0.4 ikke beskriver: en KJEDE der trinnet er poenget med hver
   * eneste rad. `BookingChain` skrev den begrunnelsen ut i sin egen kommentar og løste den ved å
   * kalle `Badge` direkte med en oppslått tone — som er nøyaktig formen `quiet-neutral-states.spec`
   * finnes for å fange, og som den fanget: rød 19. september.
   *
   * Unntaket er ekte, men det hørte ikke hjemme i en unntaksliste i testen — der ville det vært en
   * regel uten begrunnelse, i en fil ingen leser når de skriver den neste statuskolonnen. Her er
   * det et VALG kallstedet tar synlig, med typen som spør etter det.
   */
  neutralAs?: 'quiet' | 'pill';
  /**
   * `count`: tilstanden er et ANTALL som avviker — «22» som mangler (Glass 2, §16.5 punkt 10). Gi
   * `label` så skjermleseren hører hva tallet teller. En nøytral telling er fortsatt stille.
   */
  variant?: 'pill' | 'count';
  label?: string;
  className?: string;
  'data-testid'?: string;
}

export function StatusTag({
  tone,
  children,
  neutralAs = 'quiet',
  variant = 'pill',
  label,
  className,
  'data-testid': testId,
}: StatusTagProps): ReactNode {
  if (tone === 'neutral' && neutralAs === 'quiet') {
    return (
      <QuietState className={className} data-testid={testId}>
        {children}
      </QuietState>
    );
  }
  return (
    <Badge
      tone={tone}
      variant={variant}
      {...(label === undefined ? {} : { label })}
      className={className}
      data-testid={testId}
    >
      {children}
    </Badge>
  );
}
